import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";

const buttonGroupVariants = cva("inline-flex", {
  variants: {
    variant: {
      attached: "",
      separated: "",
    },
    orientation: {
      horizontal: "flex-row",
      vertical: "flex-col",
    },
    size: {
      xsmall: "",
      small: "",
      medium: "",
      large: "",
    },
    isFullWidth: {
      true: "w-full",
      false: "w-fit",
    },
    isDisabled: {
      true: "pointer-events-none opacity-50",
      false: "",
    },
  },
  compoundVariants: [
    {
      variant: "separated",
      orientation: "horizontal",
      class: "gap-2",
    },
    {
      variant: "separated",
      orientation: "vertical",
      class: "gap-2",
    },
  ],
  defaultVariants: {
    variant: "attached",
    orientation: "horizontal",
    size: "medium",
    isFullWidth: false,
    isDisabled: false,
  },
});

export interface ButtonGroupProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange">,
    VariantProps<typeof buttonGroupVariants> {
  children: React.ReactNode;
  variant?: "attached" | "separated";
  isDisabled?: boolean;
  isFullWidth?: boolean;
  /**
   * Value for controlled button group
   */
  value?: string | number;
  /**
   * Callback when value changes (for controlled mode)
   */
  onChange?: (value: string | number) => void;
}

const ButtonGroup = React.forwardRef<HTMLDivElement, ButtonGroupProps>(
  (
    {
      className,
      variant = "attached",
      orientation = "horizontal",
      size = "medium",
      isDisabled = false,
      isFullWidth = false,
      value,
      onChange,
      children,
      ...props
    },
    ref
  ) => {
    const childrenArray = React.Children.toArray(children);
    const isControlled = value !== undefined && onChange !== undefined;

    return (
      <div
        ref={ref}
        className={cn(
          buttonGroupVariants({
            variant,
            orientation,
            size,
            isDisabled,
            isFullWidth,
          }),
          className
        )}
        role="group"
        {...props}
      >
        {childrenArray.map((child, index) => {
          if (!React.isValidElement(child)) {
            return child;
          }

          const isFirst = index === 0;
          const isLast = index === childrenArray.length - 1;
          const isMiddle = !isFirst && !isLast;

          // Get value from child props for controlled mode
          const childValue = child.props.value;
          const isSelected = isControlled && childValue === value;

          // Build classes to apply border radius removal and borders
          let groupClasses = "";

          // Only apply connected styling for "attached" variant
          if (variant === "attached") {
            if (orientation === "horizontal") {
              if (isFirst) {
                groupClasses = "rounded-r-none border-r-0";
              } else if (isLast) {
                groupClasses = "rounded-l-none";
              } else if (isMiddle) {
                groupClasses = "rounded-none border-r-0";
              }
            } else {
              // vertical
              if (isFirst) {
                groupClasses = "rounded-b-none border-b-0";
              } else if (isLast) {
                groupClasses = "rounded-t-none";
              } else if (isMiddle) {
                groupClasses = "rounded-none border-b-0";
              }
            }
          }

          // Determine the variant to use
          const childVariant = child.props.variant;
          const hasExplicitVariant = childVariant !== undefined;

          // For controlled mode with explicit variant, maintain the variant
          // and add styling classes for selected state
          let finalVariant = childVariant;
          let selectedStateClasses = "";

          if (isControlled) {
            if (hasExplicitVariant) {
              // Keep the child's variant and add selected state styling
              if (isSelected && childVariant === "tertiary") {
                selectedStateClasses = "bg-action-fill-primary-faded";
              } else if (isSelected && childVariant === "secondary") {
                selectedStateClasses =
                  "bg-action-fill-primary-faded border-action-outline-primary-faded";
              }
            } else {
              // No explicit variant: use primary for selected, secondary for unselected
              finalVariant = isSelected ? "primary" : "secondary";
            }
          }

          // Clone child and add our classes and handlers
          return React.cloneElement(child, {
            ...child.props,
            className: cn(
              child.props.className,
              groupClasses,
              isFullWidth && "flex-1",
              // For attached variant, ensure proper layering
              variant === "attached" && "relative",
              variant === "attached" && !isSelected && "hover:z-10 focus:z-10",
              variant === "attached" && isSelected && "z-20",
              // Apply selected state classes for explicit variants
              selectedStateClasses
            ),
            disabled: isDisabled || child.props.disabled,
            size: size || child.props.size,
            // If controlled and has value, handle click
            onClick:
              isControlled && childValue !== undefined
                ? (e: React.MouseEvent) => {
                    child.props.onClick?.(e);
                    if (!isDisabled && !child.props.disabled) {
                      onChange(childValue);
                    }
                  }
                : child.props.onClick,
            variant: finalVariant,
          } as any);
        })}
      </div>
    );
  }
);

ButtonGroup.displayName = "ButtonGroup";

export { ButtonGroup, buttonGroupVariants };
